import { useJourneyDynamicDetails, useJourneyStaticDetails } from "../../context";
import { FC, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { StepProps } from "src/Modules/Flights/constants/JourneyDetails";
import PaymentOptions from "./components/PaymentOptions";
import PaymobPayment from "./components/PaymobPayment/PaymobPayment";
import { useInitiateOrder, computeBookingFlags, hasBookingFlags } from "./hooks/useInitiateOrder";
import useTotalFare from "../../hooks/useTotalFare";
import useStepFlow from "src/Modules/Flights/hooks/useStepFlow";
import { PAYMENT_FLOWS, PAYMENT_METHODS, type FailContext } from "./constants";

const Payment: FC<StepProps> = (_) => {
  const { t } = useTranslation();
  const { journeyDetails } = useJourneyStaticDetails();
  const currency = journeyDetails.data?.fare?.currency || t("Flights.details.payment.currencyFallback");
  const { ticketId, searchId } = useParams();
  const navigate = useNavigate();
  const { goToStep } = useStepFlow();
  const { runInitiate, isLoading: isInitiateRunning, refetchDetails } = useInitiateOrder();
  const { payment } = useJourneyDynamicDetails();
  const { pointsFactor } = useTotalFare();
  const { selectedPayment, mixError } = payment;
  const [failureNode, setFailureNode] = useState<ReactNode>(null);
  const [isPaymentStep, setIsPaymentStep] = useState(false);
  const [paymentData, setPaymentData] = useState<{provider:string, clientSecret:string}>();

  const handleConfirmSelection = async () => {
    if (!selectedPayment || mixError) return;
    setFailureNode(null);
    const usedFlow = PAYMENT_FLOWS.find((flow) => flow.check(selectedPayment));
    if (!usedFlow) return;
    const res = await usedFlow.execute(runInitiate, selectedPayment);
    if (res?.data?.orderId && !hasBookingFlags(res?.data)) {
      setPaymentData({provider:"paymob", clientSecret:res.data.clientSecret});
      usedFlow.onSuccess({
        navigate,
        ticketId,
        orderId: res.data.orderId,
        clientSecret: res.data.clientSecret,
        setIsPaymentStep,
      });
    } else {
      const flags = computeBookingFlags(res?.data, res?.error);
      const oldFare = journeyDetails.data?.fare?.publishedFare;
      const newFare = res?.data?.newFare?.publishedFare ?? 0;
      const failCtx: FailContext = {
        navigateStep: goToStep,
        goToListing: () => navigate(`/travel/tickets/${searchId}`),
        reBook: handleConfirmSelection,
        refetchDetails,
        getFare: () => ({ oldFare: oldFare as number, newFare: flags.isPriceChanged ? newFare : (oldFare as number) }),
        isLoading: isInitiateRunning,
        flags,
      };
      setFailureNode(usedFlow.onFail?.(failCtx) ?? null);
    }
  };

  return (
    <>
      {isPaymentStep ? (
        <PaymobPayment paymentData={paymentData} setIsPaymentStep={setIsPaymentStep} selectedPayment={selectedPayment} currency={currency}/>
      ) : (
        <div className="flex w-full flex-col gap-4">
          <PaymentOptions payment={payment} currency={currency} pointsFactor={pointsFactor} disableAll={isInitiateRunning}/>
          <div className="flex items-center justify-center">
            <Button
              onClick={handleConfirmSelection}
              disabled={!selectedPayment || !!mixError}
              loading={isInitiateRunning}
              iconPlacement="end"
              className="bg-background-active! w-full! rounded-md! p-6! font-semibold! cursor-pointer! text-font-inverted! shadow-md! disabled:cursor-not-allowed! disabled:opacity-50!"
            >
              {selectedPayment && PAYMENT_METHODS[selectedPayment.paymentMethod].buttonText}
            </Button>
          </div>
        </div>
      )}
      {failureNode}
    </>
  );
};

export default Payment;
